import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { recurringInvoicesAPI, clientsAPI } from "../lib/api";
import { formatCurrency, formatDate, CURRENCIES } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Trash2, RefreshCw, Pause, Play } from "lucide-react";

const FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "bi-weekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

export default function RecurringInvoicesPage() {
  const navigate = useNavigate();
  const [recurring, setRecurring] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const [formData, setFormData] = useState({
    client_id: "",
    title: "",
    notes: "",
    frequency: "monthly",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    auto_send: false,
    lines: [],
  });

  const [showLineDialog, setShowLineDialog] = useState(false);
  const [lineForm, setLineForm] = useState({
    description: "",
    quantity: "1",
    unit_price: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recurringRes, clientsRes] = await Promise.all([
        recurringInvoicesAPI.list(),
        clientsAPI.list({ status: "active" }),
      ]);
      setRecurring(recurringRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (id) => clients.find((c) => c.id === id)?.name || "-";
  const getClientCurrency = (id) => clients.find((c) => c.id === id)?.currency || "PHP";

  const handleAddLine = () => {
    const qty = parseFloat(lineForm.quantity) || 0;
    const price = parseFloat(lineForm.unit_price) || 0;
    if (!lineForm.description || price <= 0) {
      toast.error("Please fill in description and price");
      return;
    }
    const newLine = {
      ...lineForm,
      id: `temp-${Date.now()}`,
      quantity: qty,
      unit_price: price,
      amount: qty * price,
    };
    setFormData({
      ...formData,
      lines: [...formData.lines, newLine],
    });
    setLineForm({ description: "", quantity: "1", unit_price: "" });
    setShowLineDialog(false);
  };

  const handleRemoveLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const calculateTotal = () => {
    return formData.lines.reduce((sum, line) => sum + line.quantity * line.unit_price, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.title || formData.lines.length === 0) {
      toast.error("Please fill in all required fields and add at least one line item");
      return;
    }

    try {
      const data = {
        ...formData,
        end_date: formData.end_date || null,
        lines: formData.lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
        })),
      };

      if (editing) {
        await recurringInvoicesAPI.update(editing.id, data);
        toast.success("Recurring invoice updated");
      } else {
        await recurringInvoicesAPI.create(data);
        toast.success("Recurring invoice created");
      }
      setShowDialog(false);
      setEditing(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      title: "",
      notes: "",
      frequency: "monthly",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      auto_send: false,
      lines: [],
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this recurring invoice?")) return;
    try {
      await recurringInvoicesAPI.delete(id);
      toast.success("Recurring invoice deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await recurringInvoicesAPI.update(item.id, { is_active: !item.is_active });
      toast.success(item.is_active ? "Recurring invoice paused" : "Recurring invoice activated");
      fetchData();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleGenerateInvoice = async (id) => {
    try {
      const res = await recurringInvoicesAPI.generate(id);
      toast.success("Invoice generated");
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error("Failed to generate invoice");
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      client_id: item.client_id,
      title: item.title,
      notes: item.notes || "",
      frequency: item.frequency,
      start_date: item.start_date,
      end_date: item.end_date || "",
      auto_send: item.auto_send,
      lines: item.lines || [],
    });
    setShowDialog(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recurring-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Recurring Invoices</h1>
          <p className="text-muted-foreground mt-1">
            Automate your billing for retainer clients
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowDialog(true);
          }}
          className="gap-2"
          data-testid="add-recurring-btn"
        >
          <Plus className="h-4 w-4" />
          New Recurring Invoice
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Active</p>
            <p className="text-2xl font-bold mt-1">
              {recurring.filter((r) => r.is_active).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Revenue</p>
            <p className="text-2xl font-bold mt-1 font-mono">
              {formatCurrency(
                recurring
                  .filter((r) => r.is_active && r.frequency === "monthly")
                  .reduce((sum, r) => sum + (r.total || 0), 0)
              )}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Paused</p>
            <p className="text-2xl font-bold mt-1">
              {recurring.filter((r) => !r.is_active).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : recurring.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <RefreshCw className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No recurring invoices</h3>
            <p className="text-muted-foreground mb-4">
              Set up recurring invoices for your retainer clients
            </p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Recurring Invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Generated</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recurring.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{getClientName(item.client_id)}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell className="font-mono">
                    {formatCurrency(item.total, item.currency)}
                  </TableCell>
                  <TableCell className="capitalize">{item.frequency}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"}>
                      {item.is_active ? "Active" : "Paused"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.last_generated ? formatDate(item.last_generated) : "Never"}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleGenerateInvoice(item.id)}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Generate Invoice Now
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(item)}>
                          {item.is_active ? (
                            <>
                              <Pause className="h-4 w-4 mr-2" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(item)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(item.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Recurring Invoice" : "New Recurring Invoice"}
            </DialogTitle>
            <DialogDescription>
              Set up automatic invoice generation for retainer clients
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Frequency *</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Monthly Retainer - January 2026"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Line Items *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLineDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              {formData.lines.length > 0 ? (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.lines.map((line, index) => (
                        <TableRow key={line.id || index}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell className="text-right font-mono">{line.quantity}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(line.unit_price, getClientCurrency(formData.client_id))}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              line.quantity * line.unit_price,
                              getClientCurrency(formData.client_id)
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveLine(index)}
                              className="h-8 w-8 text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg">
                  No line items added
                </p>
              )}
              {formData.lines.length > 0 && (
                <div className="text-right font-semibold">
                  Total: {formatCurrency(calculateTotal(), getClientCurrency(formData.client_id))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="auto_send"
                checked={formData.auto_send}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_send: checked })}
              />
              <Label htmlFor="auto_send">Auto-send when generated (coming soon)</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Line Dialog */}
      <Dialog open={showLineDialog} onOpenChange={setShowLineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={lineForm.description}
                onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                placeholder="Service description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={lineForm.quantity}
                  onChange={(e) =>
                    setLineForm({ ...lineForm, quantity: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Price *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={lineForm.unit_price}
                  onChange={(e) =>
                    setLineForm({ ...lineForm, unit_price: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLineDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLine}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
